import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import api from "@/lib/api";
import { useSite } from "@/lib/site";
import { Fuel, LayoutDashboard, Settings, LogOut, BarChart3, Landmark, ChevronDown, KeyRound, Loader2, Calculator, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { isAccountant, roleLabel } from "@/lib/roles";

const NAV = [
  { to: "/", label: "Shifts", icon: LayoutDashboard },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/banking", label: "Banking", icon: Landmark },
  { to: "/store", label: "Store", icon: Warehouse },
  { to: "/config", label: "Station Setup", icon: Settings },
];

export default function Layout({ children }) {
  const site = useSite();
  const { user, logout } = useAuth();
  const loc = useLocation();
  const navigate = useNavigate();
  const [changePwOpen, setChangePwOpen] = useState(false);
  const nav = [...NAV, { to: "/accounting", label: isAccountant(user) ? "Accounting" : "Debtors", icon: Calculator }];

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-brand text-white shadow-md">
        <div className="mx-auto flex max-w-[1600px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2.5" data-testid="brand-home-link">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#DD1D21]">
                <Fuel className="h-5 w-5" />
              </span>
              <div className="leading-tight">
                <div className="font-display text-sm font-bold tracking-tight">{site.short_name}</div>
                <div className="text-[10px] uppercase tracking-widest text-slate-300">Shift Reconciliation</div>
              </div>
            </Link>
            <nav className="hidden items-center gap-1 md:flex">
              {nav.map((n) => {
                const active = loc.pathname === n.to;
                const Icon = n.icon;
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                    className={`flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                      active ? "bg-white/15 text-white" : "text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {n.label}
                  </Link>
                );
              })}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/v2" className="hidden rounded-lg px-2 py-1.5 text-sm text-slate-200 hover:bg-white/10 hover:text-white md:block">Try the new design</Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-slate-200 hover:bg-white/10 hover:text-white"
                  data-testid="user-menu-trigger"
                >
                  <div className="hidden text-right sm:block">
                    <div className="text-sm font-semibold text-white">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-300">{roleLabel(user?.role)}</div>
                  </div>
                  <ChevronDown className="h-3.5 w-3.5" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={() => setChangePwOpen(true)} data-testid="change-password-menu-item">
                  <KeyRound className="mr-2 h-4 w-4" />Change Password
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={doLogout} data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" />Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">{children}</main>
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
    </div>
  );
}

function ChangePasswordDialog({ open, onOpenChange }) {
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setCurrentPassword(""); setNewPassword(""); setConfirmPassword("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: currentPassword, new_password: newPassword });
      toast.success("Password changed");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to change password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Change Password</DialogTitle>
          <DialogDescription>Update the password for your account.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <Input id="current-password" type="password" value={currentPassword} autoComplete="current-password"
              onChange={(e) => setCurrentPassword(e.target.value)} required data-testid="change-pw-current-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input id="new-password" type="password" value={newPassword} autoComplete="new-password"
              onChange={(e) => setNewPassword(e.target.value)} required minLength={6} data-testid="change-pw-new-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <Input id="confirm-password" type="password" value={confirmPassword} autoComplete="new-password"
              onChange={(e) => setConfirmPassword(e.target.value)} required minLength={6} data-testid="change-pw-confirm-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} data-testid="change-pw-submit-button">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Change Password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
